import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTheme } from '../context/ThemeContext';
import { useLanguage } from '../context/LanguageContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import {
  Send, CheckCircle, Shield, Award, Clock, Star,
  Sparkles, ArrowRight, MessageCircle, Sun, Moon, Globe, Phone, Mail
} from 'lucide-react';

const WHATSAPP_NUMBER = '491722888879';
const DION_LOGO = 'https://customer-assets.emergentagent.com/job_dion-hair/artifacts/cm40t14d_DionHairClinic_Logo.svg';
const API = process.env.REACT_APP_BACKEND_URL;

const tr = (lang, de, en) => (lang === 'de' ? de : en);

const DynamicPromoPage = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { isDark, toggleTheme } = useTheme();
  const { language, toggleLanguage } = useLanguage();
  const [partnerData, setPartnerData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [formData, setFormData] = useState({ name: '', email: '', phone: '', message: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [visibleSections, setVisibleSections] = useState({});
  const sectionRefs = useRef({});

  useEffect(() => {
    const fetchPromo = async () => {
      try {
        const res = await fetch(`${API}/api/promo/${slug}`);
        if (!res.ok) { setNotFound(true); return; }
        const data = await res.json();
        // Handle redirect for old slugs
        if (data.redirect) {
          navigate(`/promo/${data.redirect}`, { replace: true });
          return;
        }
        setPartnerData(data);
      } catch {
        setNotFound(true);
      } finally {
        setLoading(false);
      }
    };
    fetchPromo();
  }, [slug, navigate]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => entries.forEach((e) => {
        if (e.isIntersecting) setVisibleSections((p) => ({ ...p, [e.target.id]: true }));
      }),
      { threshold: 0.15, rootMargin: '0px 0px -50px 0px' }
    );
    Object.values(sectionRefs.current).forEach((ref) => ref && observer.observe(ref));
    return () => observer.disconnect();
  }, [partnerData]);

  const reg = (id) => (el) => { if (el) sectionRefs.current[id] = el; };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.email) {
      toast.error(tr(language, 'Bitte alle Pflichtfelder ausfüllen.', 'Please fill in all required fields.'));
      return;
    }
    setIsSubmitting(true);
    try {
      const res = await fetch(`${API}/api/promo/${slug}/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: formData.name,
          email: formData.email,
          phone: formData.phone || '',
          subject: `promo-${slug}`,
          message: `[PROMO: ${partnerData.promo_code} - ${partnerData.name}]\n\n${formData.message || ''}`,
        }),
      });
      if (!res.ok) throw new Error();
      setIsSuccess(true);
      toast.success(tr(language, 'Anfrage gesendet!', 'Request sent!'));
      setTimeout(() => { setIsSuccess(false); setFormData({ name: '', email: '', phone: '', message: '' }); }, 4000);
    } catch {
      toast.error(tr(language, 'Fehler beim Senden.', 'Error sending.'));
    } finally {
      setIsSubmitting(false);
    }
  };

  if (loading) return (
    <div className={`min-h-screen flex items-center justify-center ${isDark ? 'bg-[#0a0a0a]' : 'bg-white'}`}>
      <svg className={`animate-spin w-8 h-8 ${isDark ? 'text-[#7BA7C2]' : 'text-[#5A8BA6]'}`} viewBox="0 0 24 24"><circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" fill="none" strokeDasharray="60 30" /></svg>
    </div>
  );

  if (notFound) return (
    <div className={`min-h-screen flex items-center justify-center ${isDark ? 'bg-[#0a0a0a]' : 'bg-white'}`}>
      <div className="text-center">
        <h1 className={`text-4xl font-bold mb-4 ${isDark ? 'text-white' : 'text-gray-900'}`}>404</h1>
        <p className={isDark ? 'text-white/50' : 'text-gray-500'}>{tr(language, 'Promo-Seite nicht gefunden.', 'Promo page not found.')}</p>
      </div>
    </div>
  );

  const accent = partnerData.accent_color || '#D4930D';
  const discount = partnerData.discount_text || '10%';
  const promoCode = partnerData.promo_code;
  const partnerName = partnerData.name;
  const partnerLogo = partnerData.logo_url;
  const headline = partnerData.custom_headline;
  const description = partnerData.custom_description;

  const whatsappMsg = language === 'de'
    ? `Hallo, ich komme über ${partnerName} und interessiere mich für eine Haartransplantation. Mein Promo-Code: ${promoCode}`
    : `Hello, I'm coming via ${partnerName} and I'm interested in a hair transplant. My promo code: ${promoCode}`;
  const whatsappUrl = `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(whatsappMsg)}`;

  const benefits = [
    { icon: <Shield className="w-6 h-6" />, title: tr(language, 'Deutsche Qualität', 'German Quality'), desc: tr(language, 'Höchste medizinische Standards in Mönchengladbach', 'Highest medical standards in Mönchengladbach') },
    { icon: <Award className="w-6 h-6" />, title: tr(language, 'Erfahrene Spezialisten', 'Experienced Specialists'), desc: tr(language, 'Über 4.000+ erfolgreiche Behandlungen', '4,000+ successful treatments') },
    { icon: <Clock className="w-6 h-6" />, title: tr(language, 'Modernste Technologie', 'Latest Technology'), desc: tr(language, 'FUE & DHI Saphir-Technologie', 'FUE & DHI Sapphire technology') },
    { icon: <Star className="w-6 h-6" />, title: tr(language, 'Rundum-Betreuung', 'Full-Service Care'), desc: tr(language, 'Vor, während und nach dem Eingriff', 'Before, during, and after the procedure') },
  ];

  return (
    <div className={`min-h-screen transition-colors duration-500 ${isDark ? 'bg-[#0a0a0a]' : 'bg-white'}`}>
      {/* Controls */}
      <div className="fixed top-4 right-4 z-50 flex items-center gap-2">
        <button onClick={toggleLanguage} className={`p-2.5 rounded-full backdrop-blur-md transition-all ${isDark ? 'bg-white/10 hover:bg-white/20 text-white' : 'bg-black/5 hover:bg-black/10 text-gray-700'}`}><Globe className="w-4 h-4" /></button>
        <button onClick={toggleTheme} className={`p-2.5 rounded-full backdrop-blur-md transition-all ${isDark ? 'bg-white/10 hover:bg-white/20 text-white' : 'bg-black/5 hover:bg-black/10 text-gray-700'}`}>{isDark ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}</button>
      </div>

      {/* HERO */}
      <section className="relative min-h-[100dvh] flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0">
          <div className={`absolute inset-0 ${isDark ? 'bg-gradient-to-b from-[#0a0a0a] via-[#0f1114] to-[#0a0a0a]' : 'bg-gradient-to-b from-white via-gray-50 to-white'}`} />
          <div className={`absolute top-1/4 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] rounded-full blur-[180px]`} style={{ backgroundColor: `${accent}10` }} />
          <div className={`absolute inset-0 ${isDark ? 'opacity-[0.02]' : 'opacity-[0.03]'}`} style={{ backgroundImage: `linear-gradient(${accent} 1px, transparent 1px), linear-gradient(90deg, ${accent} 1px, transparent 1px)`, backgroundSize: '80px 80px' }} />
        </div>

        <div className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 pt-16 pb-12 text-center">
          {/* Logos */}
          <div className="flex items-center justify-center gap-4 sm:gap-8 mb-8">
            <img src={DION_LOGO} alt="Dion Hair Clinic" className={`h-12 sm:h-16 md:h-20 w-auto ${isDark ? 'brightness-0 invert' : ''}`} />
            {partnerLogo && (
              <>
                <span className={`text-2xl sm:text-3xl font-thin ${isDark ? 'text-white/30' : 'text-gray-300'}`}>&times;</span>
                <img src={partnerLogo} alt={partnerName} className="h-14 sm:h-20 md:h-24 w-auto object-contain" />
              </>
            )}
          </div>

          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full mb-6" style={{ backgroundColor: `${accent}20`, borderColor: `${accent}30`, borderWidth: '1px' }}>
            <Sparkles className="w-4 h-4" style={{ color: accent }} />
            <span className="text-sm font-semibold tracking-wide" style={{ color: accent }}>
              {tr(language, 'EXKLUSIVE PARTNER-AKTION', 'EXCLUSIVE PARTNER OFFER')}
            </span>
          </div>

          <h1 className={`text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold leading-tight mb-6 ${isDark ? 'text-white' : 'text-gray-900'}`}>
            {headline || (
              <>
                <span style={{ color: accent }}>{discount} </span>
                {tr(language, 'Rabatt', 'Discount')}
                <br />
                <span className={`text-[0.7em] font-medium ${isDark ? 'text-white/80' : 'text-gray-700'}`}>
                  {tr(language, 'auf deine Haartransplantation', 'on your Hair Transplant')}
                </span>
              </>
            )}
          </h1>

          <p className={`text-base sm:text-lg max-w-2xl mx-auto mb-8 leading-relaxed ${isDark ? 'text-white/60' : 'text-gray-500'}`}>
            {description || tr(language,
              `Exklusiv über ${partnerName}: Sichere dir jetzt deinen persönlichen Rabatt auf eine Haartransplantation bei der Dion Hair Clinic in Mönchengladbach.`,
              `Exclusive via ${partnerName}: Secure your personal discount on a hair transplant at Dion Hair Clinic in Mönchengladbach.`
            )}
          </p>

          {/* Promo Code */}
          <div className="inline-block rounded-2xl p-[1px] mb-10" style={{ background: `linear-gradient(135deg, ${accent}60, ${accent}30, ${accent}60)` }}>
            <div className={`rounded-2xl px-8 py-5 ${isDark ? 'bg-[#111]/95' : 'bg-white/95'}`}>
              <p className={`text-xs uppercase tracking-widest mb-2 ${isDark ? 'text-white/40' : 'text-gray-400'}`}>
                {tr(language, 'Dein Promo-Code', 'Your Promo Code')}
              </p>
              <div className="flex items-center gap-3">
                <span className="text-2xl sm:text-3xl font-mono font-bold tracking-wider" style={{ color: accent }} data-testid="dynamic-promo-code">
                  {promoCode}
                </span>
                <button onClick={() => { navigator.clipboard.writeText(promoCode); toast.success(tr(language, 'Code kopiert!', 'Code copied!')); }} className={`p-2 rounded-lg transition-all hover:scale-105 ${isDark ? 'bg-white/10 hover:bg-white/20 text-white/60' : 'bg-gray-100 hover:bg-gray-200 text-gray-500'}`}>
                  <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="9" y="9" width="13" height="13" rx="2" ry="2"/><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/></svg>
                </button>
              </div>
            </div>
          </div>

          {/* CTAs */}
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <a href={whatsappUrl} target="_blank" rel="noopener noreferrer" data-testid="dynamic-promo-whatsapp" className="group w-full sm:w-auto inline-flex items-center justify-center gap-3 px-8 py-4 rounded-full bg-gradient-to-r from-[#25D366] to-[#128C7E] text-white font-semibold text-base shadow-lg shadow-[#25D366]/20 hover:shadow-xl hover:scale-[1.02] transition-all duration-300">
              <MessageCircle className="w-5 h-5" />
              {tr(language, 'Jetzt via WhatsApp anfragen', 'Inquire via WhatsApp now')}
              <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
            </a>
            <button onClick={() => document.getElementById('dp-contact')?.scrollIntoView({ behavior: 'smooth' })} className={`w-full sm:w-auto inline-flex items-center justify-center gap-3 px-8 py-4 rounded-full font-semibold text-base border transition-all hover:scale-[1.02] ${isDark ? 'border-white/20 text-white hover:bg-white/10' : 'border-gray-300 text-gray-700 hover:bg-gray-50'}`}>
              <Mail className="w-5 h-5" />
              {tr(language, 'E-Mail schreiben', 'Send an Email')}
            </button>
          </div>
        </div>

        <div className="absolute bottom-6 left-1/2 -translate-x-1/2 animate-bounce">
          <div className={`w-6 h-10 rounded-full border-2 flex items-start justify-center pt-2 ${isDark ? 'border-white/20' : 'border-gray-300'}`}>
            <div className={`w-1.5 h-1.5 rounded-full ${isDark ? 'bg-white/40' : 'bg-gray-400'}`} />
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section id="dp-benefits" ref={reg('dp-benefits')} className={`py-20 sm:py-28 ${isDark ? 'bg-[#0f0f0f]' : 'bg-gray-50'}`}>
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className={`text-center mb-14 transition-all duration-700 ${visibleSections['dp-benefits'] ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
            <h2 className={`text-2xl sm:text-3xl md:text-4xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
              {tr(language, 'Deine Vorteile auf einen Blick', 'Your Benefits at a Glance')}
            </h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            {benefits.map((b, i) => (
              <div key={i} className={`group rounded-2xl p-6 sm:p-8 border transition-all duration-500 ${isDark ? 'bg-[#111] border-white/5 hover:border-opacity-30' : 'bg-white border-gray-100 shadow-sm'} ${visibleSections['dp-benefits'] ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`} style={{ transitionDelay: `${i * 100 + 200}ms`, ['--hover-border']: accent }}>
                <div className="flex items-start gap-4">
                  <div className="p-3 rounded-xl shrink-0 transition-colors" style={{ backgroundColor: `${accent}15`, color: accent }}>{b.icon}</div>
                  <div>
                    <h3 className={`text-lg font-semibold mb-1 ${isDark ? 'text-white' : 'text-gray-900'}`}>{b.title}</h3>
                    <p className={`text-sm leading-relaxed ${isDark ? 'text-white/50' : 'text-gray-500'}`}>{b.desc}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How it works */}
      <section id="dp-steps" ref={reg('dp-steps')} className={`py-20 sm:py-28 ${isDark ? 'bg-[#0a0a0a]' : 'bg-white'}`}>
        <div className="max-w-4xl mx-auto px-4 sm:px-6">
          <div className={`text-center mb-14 transition-all duration-700 ${visibleSections['dp-steps'] ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
            <h2 className={`text-2xl sm:text-3xl md:text-4xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
              {tr(language, "So funktioniert's", 'How it works')}
            </h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-8">
            {[
              { step: '01', title: tr(language, 'Kontakt aufnehmen', 'Get in touch'), desc: tr(language, 'Schreib uns per WhatsApp oder E-Mail mit dem Promo-Code.', 'Contact us via WhatsApp or email with the promo code.') },
              { step: '02', title: tr(language, 'Kostenlose Beratung', 'Free Consultation'), desc: tr(language, 'Wir beraten dich unverbindlich und erstellen einen Plan.', 'We advise you and create an individual plan.') },
              { step: '03', title: tr(language, `${discount} sparen`, `Save ${discount}`), desc: tr(language, 'Sichere dir deinen exklusiven Rabatt.', 'Secure your exclusive discount.') },
            ].map((s, i) => (
              <div key={i} className={`text-center transition-all duration-500 ${visibleSections['dp-steps'] ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`} style={{ transitionDelay: `${i * 150 + 200}ms` }}>
                <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl mb-4 text-white font-bold text-lg shadow-lg" style={{ background: `linear-gradient(135deg, ${accent}, ${accent}cc)`, boxShadow: `0 8px 20px ${accent}30` }}>
                  {s.step}
                </div>
                <h3 className={`text-lg font-semibold mb-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>{s.title}</h3>
                <p className={`text-sm leading-relaxed ${isDark ? 'text-white/50' : 'text-gray-500'}`}>{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact */}
      <section id="dp-contact" ref={reg('dp-contact')} className={`py-20 sm:py-28 relative ${isDark ? 'bg-[#0f0f0f]' : 'bg-gray-50'}`}>
        <div className={`absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[500px] h-[500px] rounded-full blur-[180px]`} style={{ backgroundColor: `${accent}06` }} />
        <div className="max-w-5xl mx-auto px-4 sm:px-6 relative">
          <div className={`text-center mb-14 transition-all duration-700 ${visibleSections['dp-contact'] ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
            <span className="inline-block px-4 py-2 rounded-full text-sm font-medium mb-4" style={{ backgroundColor: `${accent}15`, color: accent }}>
              {tr(language, 'Jetzt Angebot sichern', 'Secure your offer now')}
            </span>
            <h2 className={`text-2xl sm:text-3xl md:text-4xl font-bold mb-4 ${isDark ? 'text-white' : 'text-gray-900'}`}>
              {tr(language, 'Kontaktiere uns', 'Contact us')}
            </h2>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-5 gap-8">
            <div className={`lg:col-span-3 rounded-2xl p-6 sm:p-8 border transition-all duration-500 ${isDark ? 'bg-[#111] border-white/5' : 'bg-white border-gray-100 shadow-sm'} ${visibleSections['dp-contact'] ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}>
              {isSuccess ? (
                <div className="flex flex-col items-center justify-center py-12 text-center" data-testid="dynamic-promo-success">
                  <div className="w-16 h-16 rounded-full bg-gradient-to-br from-[#25D366] to-[#128C7E] flex items-center justify-center mb-4"><CheckCircle className="w-8 h-8 text-white" /></div>
                  <h3 className={`text-xl font-bold mb-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>{tr(language, 'Vielen Dank!', 'Thank you!')}</h3>
                  <p className={isDark ? 'text-white/50' : 'text-gray-500'}>{tr(language, 'Wir melden uns schnellstmöglich.', 'We will get back to you ASAP.')}</p>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-5" data-testid="dynamic-promo-form">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                    <div>
                      <Label className={isDark ? 'text-white/70' : 'text-gray-600'}>{tr(language, 'Name', 'Name')} *</Label>
                      <Input data-testid="dp-name" value={formData.name} onChange={(e) => setFormData(p => ({ ...p, name: e.target.value }))} required className={`mt-1.5 ${isDark ? 'bg-white/5 border-white/10 text-white placeholder:text-white/30' : 'bg-gray-50 border-gray-200'}`} placeholder={tr(language, 'Dein Name', 'Your name')} />
                    </div>
                    <div>
                      <Label className={isDark ? 'text-white/70' : 'text-gray-600'}>{tr(language, 'E-Mail', 'Email')} *</Label>
                      <Input data-testid="dp-email" type="email" value={formData.email} onChange={(e) => setFormData(p => ({ ...p, email: e.target.value }))} required className={`mt-1.5 ${isDark ? 'bg-white/5 border-white/10 text-white placeholder:text-white/30' : 'bg-gray-50 border-gray-200'}`} placeholder={tr(language, 'Deine E-Mail', 'Your email')} />
                    </div>
                  </div>
                  <div>
                    <Label className={isDark ? 'text-white/70' : 'text-gray-600'}>{tr(language, 'Telefon (optional)', 'Phone (optional)')}</Label>
                    <Input data-testid="dp-phone" value={formData.phone} onChange={(e) => setFormData(p => ({ ...p, phone: e.target.value }))} className={`mt-1.5 ${isDark ? 'bg-white/5 border-white/10 text-white placeholder:text-white/30' : 'bg-gray-50 border-gray-200'}`} />
                  </div>
                  <div>
                    <Label className={isDark ? 'text-white/70' : 'text-gray-600'}>{tr(language, 'Nachricht (optional)', 'Message (optional)')}</Label>
                    <Textarea data-testid="dp-message" value={formData.message} onChange={(e) => setFormData(p => ({ ...p, message: e.target.value }))} rows={4} className={`mt-1.5 resize-none ${isDark ? 'bg-white/5 border-white/10 text-white placeholder:text-white/30' : 'bg-gray-50 border-gray-200'}`} />
                  </div>
                  <div className="flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm" style={{ backgroundColor: `${accent}15`, color: accent }}>
                    <Sparkles className="w-4 h-4 shrink-0" />
                    {tr(language, `Promo-Code "${promoCode}" wird automatisch übermittelt`, `Promo code "${promoCode}" is automatically included`)}
                  </div>
                  <Button type="submit" disabled={isSubmitting} data-testid="dp-submit" className="w-full py-3 h-auto rounded-xl text-white font-semibold text-base shadow-lg transition-all hover:shadow-xl" style={{ background: `linear-gradient(135deg, ${accent}, ${accent}cc)`, boxShadow: `0 8px 20px ${accent}30` }}>
                    {isSubmitting ? <span className="flex items-center gap-2"><svg className="animate-spin w-5 h-5" viewBox="0 0 24 24"><circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" fill="none" strokeDasharray="60 30" /></svg>{tr(language, 'Wird gesendet...', 'Sending...')}</span> : <span className="flex items-center gap-2"><Send className="w-4 h-4" />{tr(language, 'Anfrage senden', 'Send Request')}</span>}
                  </Button>
                </form>
              )}
            </div>

            {/* Side panel */}
            <div className={`lg:col-span-2 space-y-5 transition-all duration-500 ${visibleSections['dp-contact'] ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`} style={{ transitionDelay: '350ms' }}>
              <a href={whatsappUrl} target="_blank" rel="noopener noreferrer" className={`block rounded-2xl p-6 border transition-all hover:scale-[1.02] ${isDark ? 'bg-[#111] border-[#25D366]/20 hover:border-[#25D366]/40' : 'bg-white border-[#25D366]/20 hover:border-[#25D366]/40 shadow-sm'}`}>
                <div className="flex items-center gap-4 mb-3">
                  <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#25D366] to-[#128C7E] flex items-center justify-center"><MessageCircle className="w-6 h-6 text-white" /></div>
                  <div>
                    <h3 className={`font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>WhatsApp</h3>
                    <p className={`text-sm ${isDark ? 'text-white/40' : 'text-gray-400'}`}>{tr(language, 'Schnellste Antwort', 'Fastest response')}</p>
                  </div>
                </div>
              </a>

              <div className={`rounded-2xl p-6 border ${isDark ? 'bg-[#111] border-white/5' : 'bg-white border-gray-100 shadow-sm'}`}>
                <div className="flex items-center gap-4 mb-3">
                  <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${isDark ? 'bg-[#7BA7C2]/10' : 'bg-[#5A8BA6]/10'}`}><Phone className="w-6 h-6 text-[#7BA7C2]" /></div>
                  <div>
                    <h3 className={`font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>{tr(language, 'Telefon', 'Phone')}</h3>
                    <a href="tel:+4921619056910" className="text-sm text-[#7BA7C2] hover:underline">+49 2161 9056910</a>
                  </div>
                </div>
                <p className={`text-sm ${isDark ? 'text-white/50' : 'text-gray-500'}`}>{tr(language, 'Mo-Fr: 09:00 - 18:00 Uhr', 'Mon-Fri: 09:00 - 18:00')}</p>
              </div>

              <div className={`rounded-2xl p-6 border text-center ${isDark ? 'bg-[#111] border-white/5' : 'bg-white border-gray-100 shadow-sm'}`}>
                <div className="flex items-center justify-center gap-1 mb-2">
                  {[...Array(5)].map((_, i) => (<Star key={i} className="w-5 h-5" style={{ fill: accent, color: accent }} />))}
                </div>
                <p className={`text-sm font-medium ${isDark ? 'text-white/70' : 'text-gray-600'}`}>5.0/5 Google {tr(language, 'Bewertung', 'Rating')}</p>
                <p className={`text-xs mt-1 ${isDark ? 'text-white/30' : 'text-gray-400'}`}>{tr(language, 'Über 140 echte Bewertungen', 'Over 140 real reviews')}</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className={`py-10 border-t ${isDark ? 'bg-[#0a0a0a] border-white/5' : 'bg-white border-gray-100'}`}>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 text-center">
          <img src={DION_LOGO} alt="Dion Hair Clinic" className={`h-8 w-auto mx-auto mb-4 ${isDark ? 'brightness-0 invert' : ''}`} />
          <p className={`text-sm mb-2 ${isDark ? 'text-white/40' : 'text-gray-400'}`}>Dion Hair Clinic &middot; {tr(language, 'Mönchengladbach, Deutschland', 'Mönchengladbach, Germany')}</p>
          <div className="flex items-center justify-center gap-4 text-xs">
            <a href="/impressum" className={isDark ? 'text-white/30 hover:text-white/60' : 'text-gray-400 hover:text-gray-600'}>{tr(language, 'Impressum', 'Legal Notice')}</a>
            <span className={isDark ? 'text-white/10' : 'text-gray-200'}>|</span>
            <a href="/datenschutz" className={isDark ? 'text-white/30 hover:text-white/60' : 'text-gray-400 hover:text-gray-600'}>{tr(language, 'Datenschutz', 'Privacy')}</a>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default DynamicPromoPage;
